import React, { useCallback, useMemo } from 'react';
import { FilterBar, FilterDefinition } from './common/FilterControls';
import { Item } from '../api/generated/model';
import {
  useGetReportsIncomeFilterOptionsProgramNames,
  useGetReportsIncomeFilterOptionsStates,
  useGetReportsIncomeFilterOptionsSalespeople,
  useGetReportsIncomeFilterOptionsSalesLeads,
  getCustomers,
} from '../api/generated/arrayscapeGamingDashboardAPI';
import type { Customer } from '../api/generated/model';

export interface FinancialReportsFiltersState {
  income_date_field?: string;   // invoice_date | due_date | booking_date | program_date
  group_by?: string;            // day | week | month | quarter | year
  item_ids?: string;            // comma-separated Item IDs
  program_names?: string;       // comma-separated program names
  customer_ids?: string;        // comma-separated customer IDs
  customer_name?: string;       // free-text substring
  salesperson_names?: string;   // comma-separated attribution names (role=salesperson)
  sales_lead_names?: string;    // comma-separated attribution names (role=sales_lead)
  state_codes?: string;         // comma-separated 2-letter state codes
}

interface FinancialReportsFilterBarProps {
  filters: FinancialReportsFiltersState;
  items: Item[];
  onFiltersChange: (filters: FinancialReportsFiltersState) => void;
  onClearFilters: () => void;
}

const FinancialReportsFilterBar: React.FC<FinancialReportsFilterBarProps> = ({
  filters, items, onFiltersChange, onClearFilters,
}) => {
  // Program items: items the backend has flagged is_program (i.e. they
  // appear on at least one invoice line carrying a program_id). Used to
  // (a) feed the "All programs" quick-pick and (b) restrict the
  // Product/Service multiselect to program items when the user has
  // chosen date_field=program_date — non-program items contribute $0
  // in that mode, so showing them would just be a footgun.
  const isProgramMode = filters.income_date_field === 'program_date';
  const programItems = useMemo(
    () => items.filter(i => i.id && i.name && i.is_program),
    [items],
  );
  const programItemIDs = useMemo(() => programItems.map(i => i.id!), [programItems]);

  // Filter-options endpoints. Each returns {data: [{value, label}]}
  // (small lists, no pagination). Customers is paginated and large, so
  // it goes through the AsyncMultiPicker (defined below in filterDefs)
  // which hits /customers on demand instead of preloading.
  const { data: programNamesResp } = useGetReportsIncomeFilterOptionsProgramNames();
  const { data: statesResp } = useGetReportsIncomeFilterOptionsStates();
  const { data: salespeopleResp } = useGetReportsIncomeFilterOptionsSalespeople();
  const { data: salesLeadsResp } = useGetReportsIncomeFilterOptionsSalesLeads();

  const programNameOptions = useMemo(
    () => (programNamesResp?.data ?? [])
      .filter(o => o.value && o.label)
      .map(o => ({ value: o.value!, label: o.label! })),
    [programNamesResp],
  );
  const stateOptions = useMemo(
    () => (statesResp?.data ?? [])
      .filter(o => o.value && o.label)
      .map(o => ({ value: o.value!, label: o.label! })),
    [statesResp],
  );
  const salespersonOptions = useMemo(
    () => (salespeopleResp?.data ?? [])
      .filter(o => o.value && o.label)
      .map(o => ({ value: o.value!, label: o.label! })),
    [salespeopleResp],
  );
  const salesLeadOptions = useMemo(
    () => (salesLeadsResp?.data ?? [])
      .filter(o => o.value && o.label)
      .map(o => ({ value: o.value!, label: o.label! })),
    [salesLeadsResp],
  );
  // AsyncMultiPicker callbacks for the customer filter. Page loader
  // hits /customers with name= for the search; loadByIds resolves
  // labels for whatever IDs are already in the value (e.g. URL state).
  // Both go through the same paginated cursor — no preloading.
  const customerLoadPage = useCallback(
    async ({ search, cursor, limit }: { search: string; cursor?: string; limit: number }) => {
      const result: any = await getCustomers({
        ...(cursor ? { cursor } : {
          ...(search ? { name: search } : {}),
          sort_field: 'name' as any,
          sort_direction: 'asc' as any,
        }),
        limit,
      });
      return {
        items: (result?.data ?? []) as Customer[],
        // Backend's Pagination schema names this `cursor`, not next_cursor.
        nextCursor: result?.pagination?.cursor as string | undefined,
      };
    },
    [],
  );
  const customerLoadByIds = useCallback(
    async (ids: string[]) => {
      if (ids.length === 0) return [];
      // sort_field/sort_direction are required by the cursor library on any
      // request without a cursor, even a fixed-id lookup with no opinion about
      // order. Without them this 400s and selected customers render as bare
      // uuids instead of names.
      const result: any = await getCustomers({
        ids: ids.join(','),
        sort_field: 'name' as any,
        sort_direction: 'asc' as any,
        limit: ids.length,
      } as any);
      return (result?.data ?? []) as Customer[];
    },
    [],
  );

  const filterDefs: FilterDefinition[] = useMemo(() => {
    const visibleItems = isProgramMode ? programItems : items.filter(i => i.id && i.name);
    return [
      {
        type: 'select', field: 'income_date_field', label: 'Income by', icon: 'bi-calendar-event',
        required: true,
        options: [
          { value: 'invoice_date', label: 'Invoice Date' },
          { value: 'due_date', label: 'Due Date' },
          { value: 'booking_date', label: 'Booking Date' },
          { value: 'program_date', label: 'Program Date' },
        ],
      },
      {
        type: 'select', field: 'group_by', label: 'Group by', icon: 'bi-bar-chart',
        required: true,
        options: [
          { value: 'day', label: 'Per Day' },
          { value: 'week', label: 'Per Week' },
          { value: 'month', label: 'Per Month' },
          { value: 'quarter', label: 'Per Quarter' },
          { value: 'year', label: 'Per Year' },
        ],
      },
      {
        type: 'multiselect', field: 'item_ids', label: 'Product/Service', icon: 'bi-tag',
        options: visibleItems.map(i => ({ value: i.id!, label: i.name! })),
        // In program_date mode the dropdown is already restricted to program
        // items, so "All programs" is identical to the built-in "Select all".
        // Only surface it as a shortcut when the dropdown also contains
        // non-program items.
        quickPicks: !isProgramMode && programItemIDs.length > 0
          ? [{ label: 'All programs', values: programItemIDs }]
          : undefined,
      },
      {
        type: 'multiselect', field: 'program_names', label: 'Program', icon: 'bi-book',
        options: programNameOptions,
      },
      {
        type: 'multiselect', field: 'customer_ids', label: 'Customer', icon: 'bi-building',
        placeholder: 'Search customers…',
        loadPage: customerLoadPage,
        loadByIds: customerLoadByIds,
        getOptionValue: (c: Customer) => c.id || '',
        getOptionLabel: (c: Customer) => c.name || c.id || '',
      },
      {
        type: 'text', field: 'customer_name', label: 'Customer name', icon: 'bi-search',
        placeholder: 'e.g. Queens',
      },
      {
        type: 'multiselect', field: 'salesperson_names', label: 'Salesperson', icon: 'bi-person',
        options: salespersonOptions,
      },
      {
        type: 'multiselect', field: 'sales_lead_names', label: 'Sales Lead', icon: 'bi-person-badge',
        options: salesLeadOptions,
      },
      {
        type: 'multiselect', field: 'state_codes', label: 'State', icon: 'bi-geo-alt',
        options: stateOptions,
      },
    ];
  }, [items, programItems, programItemIDs, isProgramMode, programNameOptions, customerLoadPage, customerLoadByIds, salespersonOptions, salesLeadOptions, stateOptions]);

  const handleFilterChange = (field: string, value: string | undefined) => {
    // Don't coerce '' → undefined here. The MultiSelectFilter uses '' to
    // signal "Deselect all" (explicit empty state, distinct from the
    // default "all selected" which is undefined). Page state must
    // preserve that distinction.
    const next: FinancialReportsFiltersState = { ...filters, [field]: value };

    // Switching to program_date mode while non-program items are
    // selected leaves the user with invisible filter chips — those
    // items don't appear in the restricted dropdown but still gate
    // the query. Strip them up front so the chip set matches what
    // the picker shows.
    if (field === 'income_date_field' && value === 'program_date' && next.item_ids) {
      const allowed = new Set(programItemIDs);
      const kept = next.item_ids.split(',').filter(id => allowed.has(id.trim()));
      next.item_ids = kept.length > 0 ? kept.join(',') : undefined;
    }

    onFiltersChange(next);
  };

  return (
    <FilterBar
      filters={filters}
      filterDefs={filterDefs}
      onFilterChange={handleFilterChange}
      onClearFilters={onClearFilters}
    />
  );
};

export default FinancialReportsFilterBar;
